import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home } from "lucide-react";
import Footer from "../../components/landing/Footer";
import HeroSlider from "../../components/media_and_events/HeroSlider";
import ConferenceFilters from "../../components/media_and_events/ConferenceFilters";
import ConferenceCard from "../../components/media_and_events/ConferenceCard";
import WhyAttendCarousel from "../../components/media_and_events/WhyAttendCarousel";
import NewsletterSubscription from "../../components/media_and_events/NewsletterSubscription";
import Images from "../../utils/Images";
import conferenceAndTradeMissionService from "../../api/ConferenceAndTradeMission";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";

export default function ConferenceTradeMission() {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const heroImages = [Images.hero_img_web_three, Images.hero_img_web_four];
  const {isAuthenticated} = useAppSelector((s) => s.auth);

  // Fetch conferences and trade missions
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response =
          await conferenceAndTradeMissionService.getConferenceAndTradeMission();
        if (response.data?.status_code === 200) {
          setConferences(response?.data?.data || []);
        } else {
          toast.error(
            response?.data?.reason ||
              "Failed to load conferences and trade missions"
          );
          setConferences([]);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.reason ||
            "An error occurred while fetching data"
        );
        setConferences([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter and search logic
  const filteredConferences = useMemo(() => {
    let filtered = [...conferences];

    // Apply type filter
    if (selectedFilter !== "all") {
      filtered = filtered.filter((item) => {
        const eventTypes = item.event_type || [];
        const hasConference = eventTypes.some(
          (type) => type?.toLowerCase() === "conference"
        );
        const hasTradeMission = eventTypes.some(
          (type) =>
            type?.toLowerCase() === "trade_mission" ||
            type?.toLowerCase() === "trade_missions"
        );

        if (selectedFilter === "conferences") {
          return hasConference;
        }
        if (selectedFilter === "trade_missions") {
          return hasTradeMission;
        }
        return true;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const title = (item.event_title || "").toLowerCase();
        const description = (item.about_event || "").toLowerCase();
        const venue = (item.venue_name || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const country = (item.country || "").toLowerCase();
        
        return (
          title.includes(query) ||
          description.includes(query) ||
          venue.includes(query) ||
          city.includes(query) ||
          country.includes(query)
        );
      });
    }

    return filtered;
  }, [conferences, selectedFilter, searchQuery]);

  const handleCardClick = (item) => {
    if (item.slug) {
      navigate(`/event-details/${item.slug}`);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <HeroSlider images={heroImages} showBackButton={false} backLink="/media-events" backLabel="Back to Media & Events" />

      {/* Content Section */}
      <section className="px-4 py-12 bg-gradient-to-b from-gray-50 to-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-8xl">
          <div className="flex gap-4 justify-between items-center mb-8">
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Conference & Trade Mission
              </h2>
              <div className="mt-2 w-32 h-1 bg-emerald-500 rounded" />
            </div>
            <motion.button
              onClick={() => navigate("/media-events")}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg shadow-sm transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300"
            >
              <Home size={16} />
              Back to Main Menu
            </motion.button>
          </div>

          {/* Filters and Search */}
          <div className="mb-8">
            <ConferenceFilters
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col gap-4 items-center">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full border-b-2 border-emerald-500 animate-spin"></div>
                  <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-emerald-200 animate-pulse"></div>
                </div>
                <p className="text-sm text-gray-600">
                  Loading conferences and trade missions...
                </p>
              </div>
            </div>
          ) : filteredConferences.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col gap-4 items-center text-center">
                <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    No results found
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {searchQuery
                      ? "Try adjusting your search or filter criteria."
                      : "No conferences or trade missions available at the moment."}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${selectedFilter}-${searchQuery}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 sm:grid-cols-2 lg:grid-cols-4"
              >
                {filteredConferences.map((item, index) => (
                  <motion.div
                    key={item.id || item._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="w-full"
                  >
                    <ConferenceCard
                      item={item}
                      onClick={() => handleCardClick(item)}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}

          {/* Results count */}
          {!loading && filteredConferences.length > 0 && (
            <div className="mt-6 text-sm text-gray-600">
              Showing {filteredConferences.length} of {conferences.length}{" "}
              results
            </div>
          )}
        </div>
      </section>

      {/* Why Attend Carousel Section */}
      {!isAuthenticated && <WhyAttendCarousel />}

      {/* Newsletter Subscription Section */}
      {!isAuthenticated && <NewsletterSubscription />}

      {/* Footer */}
      <Footer />
    </div>
  );
}
